import React, { useEffect, useState } from "react";
import { Col, Row, Card, Button, Form, Input, Alert } from "antd";
import { toast } from "react-toastify";
import { UserOutlined } from "@ant-design/icons";
import { completeResetPassword, fetchUser, fetchUserByUsername, requestResetPassword } from "../../features/user/userAPI";
import { getOrganisationDetails } from "../../features/organisations/organisationsSlice";
import { useNavigate } from "react-router-dom";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [isEmail, setIsEmail] = useState(true);
    const [codeRequested, setCodeRequested] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resendSuccessMessage, setResendSuccessMessage] = useState(
        "Verification code has been sent to your email, please check your junk mail"
    );
    const [countdown, setCountdown] = useState(60);
    const [isResendDisabled, setIsResendDisabled] = useState(false);
    const [username, setUsername] = useState("");

    useEffect(() => {
        if (isResendDisabled && countdown > 0) {
            const timerId = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timerId);
        } else if (countdown === 0) {
            setIsResendDisabled(false);
            setCountdown(60);
        }
    }, [countdown, isResendDisabled]);

    const handleUsernameChange = (e) => {
        const identifier = e.target.value;
        const emailRegex = /\S+@\S+\.\S+/;
        setIsEmail(emailRegex.test(identifier));
        setUsername(identifier);
    };

    const resetHandler = async (values) => {
        setLoading(true);
        try {
            if (!codeRequested) {
                await startPasswordReset(values);
            } else {
                await finishPasswordReset(values);
            }
        } catch (error) {
            console.error("Error resetting password:", error);
            toast("Failed to reset password", { type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleResend = (event) => {
        event.preventDefault();
        event.stopPropagation();
        startPasswordReset({ username });
    };

    const startPasswordReset = async (values) => {
        try {
            const { slug } = await getOrganisationDetails();
            const identifier = isEmail ? values.username : `${slug}-${values.username}`;

            // check if user has phone number or email setup
            const user = await fetchUserByUsername(identifier);
            if (user) {
                if (user.attributes.phone) {
                    setResendSuccessMessage("Verification code has been sent to your phone number");
                }
            }

            const response = await requestResetPassword(identifier);
            setCodeRequested(true);
            setIsResendDisabled(true);
            setCountdown(60);
            console.log(response);
        } catch (error) {
            console.error("Error starting password reset:", error);
            toast("Failed to request password reset", { type: "error" });
        }
    };

    const finishPasswordReset = async (values) => {
        try {
            console.log("Received values of form: ", values);
            const response = await completeResetPassword(values);
            setCodeRequested(false);
            navigate("/login")
            console.log(response);
        } catch (error) {
            console.error("Error finishing password reset:", error);
            toast("Failed to complete password reset", { type: "error" });
        }
    };

    return (
        <div className="Login">
            <div className="AppContainer">
                <Row justify="center" align="middle">
                    <Col xs={24} md={14} lg={10}>
                        <Card>
                            <h1 className="SectionTitle">Reset Password</h1>
                            {codeRequested && <Alert style={{ marginBottom: 20 }} message={resendSuccessMessage} type="success" />}
                            <Form name="normal_login" className="login-form" initialValues={{}} size="large" onFinish={resetHandler}>
                                {codeRequested ? (
                                    <>
                                        <Form.Item
                                            name="code"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please input the code sent to your email!",
                                                },
                                            ]}
                                        >
                                            <Input prefix={<UserOutlined className="site-form-item-icon" />} placeholder="Code" />
                                        </Form.Item>
                                        <Form.Item
                                            name="password"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please input your new password!",
                                                },
                                            ]}
                                        >
                                            <Input
                                                prefix={<UserOutlined className="site-form-item-icon" />}
                                                placeholder="New Password"
                                                type="password"
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name="passwordConfirmation"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please confirm your new password!",
                                                },
                                            ]}
                                        >
                                            <Input
                                                prefix={<UserOutlined className="site-form-item-icon" />}
                                                placeholder="Confirm Password"
                                                type="password"
                                            />
                                        </Form.Item>
                                    </>
                                ) : (
                                    <Form.Item
                                        name="username"
                                        rules={[
                                            {
                                                required: true,
                                                type: "username",
                                                message: "Please input your Username/Email!",
                                            },
                                        ]}
                                    >
                                        <Input
                                            prefix={<UserOutlined className="site-form-item-icon" />}
                                            placeholder="Username / DA Number / Email"
                                            onChange={handleUsernameChange}
                                        />
                                    </Form.Item>
                                )}

                                {codeRequested && (
                                    <Form.Item>
                                        <div style={{ width: "100%", display: "flex", justifyContent: "center", gap: "10px" }}>
                                            Didn't receive code?{" "}
                                            <Button type="button" onClick={handleResend} disabled={isResendDisabled} size="small">
                                                {isResendDisabled ? `Resend code in ${countdown} seconds` : "Resend Code"}
                                            </Button>
                                        </div>
                                    </Form.Item>
                                )}
                                <Form.Item>
                                    <Button type="primary" htmlType="submit" className="login-form-button" loading={loading}>
                                        {codeRequested ? "Update Password" : "Reset Password"}
                                    </Button>
                                </Form.Item>
                                <Form.Item>
                                    <div style={{ textAlign: "center", width: "100%" }}>
                                        <a href="/login">Login Now!</a>
                                    </div>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
}
